import { useCallback, useRef, type CSSProperties, type ReactNode, type RefCallback } from 'react';

type Medidas = Pick<HTMLElement, 'scrollWidth' | 'clientWidth' | 'scrollHeight' | 'clientHeight'>;

/** A área rola de fato? (1 px de folga: o arredondamento das larguras dá 1 px de "rolagem" que não é rolagem.) */
export function precisaDoTeclado(m: Medidas): boolean {
  return m.scrollWidth > m.clientWidth + 1 || m.scrollHeight > m.clientHeight + 1;
}

/**
 * Item 48 (WCAG 2.1.1, regra scrollable-region-focusable do axe): a área entra na ordem do Tab (tabindex 0) SÓ enquanto
 * rola de fato, para quem não usa mouse rolar com as setas; sem rolagem, volta a {@code semRolagem} (sem tabindex, ou
 * "-1" quando a área também é o alvo do link "Ir para o conteúdo"). A medida acompanha o tamanho da área e o dos filhos
 * (ResizeObserver); sem ele (navegador antigo), fica como está.
 */
export function useFocoQuandoRola<T extends HTMLElement>(semRolagem: '-1' | null = null): RefCallback<T> {
  const parar = useRef<(() => void) | null>(null);
  return useCallback(
    (el: T | null) => {
      parar.current?.();
      parar.current = null;
      if (!el || typeof ResizeObserver === 'undefined') return;
      const medir = (): void => {
        if (precisaDoTeclado(el)) el.setAttribute('tabindex', '0');
        else if (semRolagem === null) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', semRolagem);
      };
      const ro = new ResizeObserver(medir);
      const observarFilhos = (): void => {
        for (const filho of Array.from(el.children)) ro.observe(filho);
      };
      ro.observe(el);
      observarFilhos();
      // A tela troca o conteúdo sem mudar o tamanho da área (o #screens troca de filho a cada rota): os filhos novos
      // passam a ser observados e a medida é refeita.
      const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(() => {
        observarFilhos();
        medir();
      });
      mo?.observe(el, { childList: true });
      parar.current = () => {
        ro.disconnect();
        mo?.disconnect();
      };
      medir();
    },
    [semRolagem],
  );
}

/**
 * Uma área que rola (tabela larga no celular, quadro de colunas): região com nome, alcançável pelo teclado enquanto
 * rola (useFocoQuandoRola). Uma tabela que cabe na tela não vira uma parada de Tab inútil.
 */
export function AreaRolavel({
  rotulo,
  className,
  style,
  children,
}: {
  rotulo: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}): JSX.Element {
  const ref = useFocoQuandoRola<HTMLDivElement>();
  return (
    <div ref={ref} className={className} style={style} role="region" aria-label={rotulo}>
      {children}
    </div>
  );
}
